import React from "react";
import {
  useDeleteExportFilesMutation,
  useExportContactsMutation,
  useGetExportFilesQuery,
  useLazyDownloadExportFileQuery,
} from "../store/slices/contactsSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";
import { toast } from "react-toastify";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";

const exportContactsHelpRules = [
  "Create an export to download the current contacts CSV for this account.",
  "The file can be used as a reference for import headers and existing contact field names.",
  "Exported columns reflect the current contact data and account contact fields at the time the export is created.",
  "When importing a CSV later, column order does not matter because headers are matched by name.",
  "If an import file contains new columns, the import review step can create matching custom contact fields.",
];

type ExportedFileType = {
  file: string;
  uuid: string;
  createdAt: string;
  createdBy: string;
  status: string;
  stats: {
    contacts: number;
    email: number;
    rows: number;
    sms: number;
    voice: number;
  };
};

export default function ExportContacts() {
  const confirmDialog = useConfirmDialog() as
    | ((title: string, message: string) => Promise<boolean>)
    | null;
  const [exportContacts, { isLoading: isExporting }] =
    useExportContactsMutation();
  const [downloadExportFile] = useLazyDownloadExportFileQuery();
  const [deleteExportFile] = useDeleteExportFilesMutation();
  const {
    data: exportedFilesResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetExportFilesQuery();

  const handleExport = async () => {
    try {
      await exportContacts().unwrap();
      refetch();
      toast.success(
        "Export started successfully. Please refresh the table in a few moments."
      );
    } catch (err) {
      console.error("Export failed", err);
    }
  };

  const handleDownload = async (item: ExportedFileType) => {
    try {
      const fileBlob = await downloadExportFile(item.uuid).unwrap();
      const objectUrl = URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = item.file || `${item.uuid}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  const handleDelete = async (uuid: string) => {
    if (!confirmDialog) {
      return;
    }
    if (
      !(await confirmDialog(
        "Delete exported file",
        "Are you sure you want to delete this exported file?"
      ))
    ) {
      return;
    }
    try {
      await deleteExportFile([uuid]).unwrap();
      refetch();
      toast.success("Exported file deleted successfully.");
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  const exportedFiles: ExportedFileType[] = Array.isArray(exportedFilesResponse)
    ? exportedFilesResponse
    : exportedFilesResponse?.files || [];

  return (
    <div className="p-3 bg-white rounded-3">
      <FullScreenLoadingIndicator
        loading={isLoading || isFetching || isExporting}
      />
      <div className="card border-0 mb-3">
        <div className="card-body bg-white rounded-3">
          <div className="fw-bold mb-2">Using exported contacts</div>
          <ul className="mb-0 ps-3 small">
            {exportContactsHelpRules.map((rule) => (
              <li key={rule}>{rule}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="d-flex justify-content-end">
        <button
          className="btn btn-colored mb-3"
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? "Exporting..." : "Export Contacts"}
        </button>
      </div>

      <h3>
        Exported Files{" "}
        <button
          className="btn btn-outline-secondary border-0 py-0 fw-bold ms-2"
          onClick={refetch}
        >
          <FontAwesomeIcon icon={faSync} />
        </button>
      </h3>

      {!isLoading && !isFetching && exportedFiles.length === 0 && (
        <div className="text-muted">No exported files found.</div>
      )}

      {exportedFiles.length > 0 && (
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>File</th>
                <th>Created At</th>
                <th>Created By</th>
                <th>Status</th>
                <th>Rows</th>
                <th>Contacts</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {exportedFiles.map((item) => (
                <tr key={item.uuid}>
                  <td className="text-nowrap">{item.file}</td>
                  <td className="text-nowrap">{item.createdAt}</td>
                  <td className="text-nowrap">{item.createdBy}</td>
                  <td>{item.status}</td>
                  <td>{item.stats?.rows ?? 0}</td>
                  <td>{item.stats?.contacts ?? 0}</td>
                  <td>
                    {item.status === "processed" ? (
                      <div className="d-flex gap-2 align-items-center flex-nowrap text-nowrap justify-content-end">
                        <button
                          className="btn btn-colored text-white"
                          onClick={() => handleDownload(item)}
                        >
                          Download
                        </button>
                        <button
                          className="btn btn-colored text-white"
                          onClick={() => handleDelete(item.uuid)}
                        >
                          Delete
                        </button>
                      </div>
                    ) : (
                      <div className="text-center">
                        Processing...
                        <button
                          className="btn btn-outline-secondary border-0 py-0 fw-bold ms-2"
                          onClick={refetch}
                        >
                          <FontAwesomeIcon icon={faSync} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
